import React from 'react';
import {Link} from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiTrendingUp,FiZap,FiTarget,FiCheck,FiArrowRight,FiSettings,FiLayers,FiGitBranch,FiBarChart3,FiShield}=FiIcons;

function DigitalTransformation() {
  const services=[ 
    {icon: FiSettings,title: 'Business Process Automation',description: 'Streamline repetitive tasks and workflows to free up your team for strategic work.',benefits: [ 
      'Comprehensive process analysis and optimization',
      'Custom automation workflows designed for your business',
      'Integration between existing tools and systems',
      'Staff training and change management support',
      '60-90% reduction in manual task time' 
    ],idealFor: 'Businesses spending 20+ hours/week on repetitive administrative tasks'},
    {icon: FiLayers,title: 'System Integration & Optimization',description: 'Connect your disconnected tools and systems for seamless data flow and reporting.',benefits: [ 
      'Complete system audit and integration planning',
      'Custom integrations between your business tools',
      'Unified reporting and analytics dashboard',
      'Data migration and cleanup services',
      'Real-time business intelligence and insights' 
    ],idealFor: 'Companies using 5+ different software tools that don\'t communicate effectively'},
    {icon: FiGitBranch,title: 'Digital Workflow Design',description: 'Design and implement efficient digital workflows that scale with your growth.',benefits: [ 
      'Custom workflow design based on your business model',
      'Automated approval and notification systems',
      'Performance tracking and optimization tools',
      'Team collaboration and communication improvements',
      'Scalable systems that grow with your business' 
    ],idealFor: 'Growing businesses struggling with process consistency and scalability'} 
  ];

  const results=[ 
    {company: 'Manufacturing Company (50 employees)',improvements: [ 
      'Reduced order processing time by 75%',
      'Eliminated 90% of data entry errors',
      'Increased customer satisfaction scores by 40%',
      'ROI: 340% in first year' 
    ]},
    {company: 'Professional Services Firm (25 employees)',improvements: [ 
      'Automated 80% of client onboarding process',
      'Reduced project delivery time by 45%',
      'Improved team productivity by 60%',
      'ROI: 280% in first year' 
    ]},
    {company: 'E-commerce Business (15 employees)',improvements: [ 
      'Integrated inventory,sales,and accounting systems',
      'Reduced manual reporting time by 85%',
      'Increased order fulfillment speed by 50%',
      'ROI: 420% in first year' 
    ]} 
  ];

  return ( 
    <div> 
      {/* Hero Section */} 
      <section className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-20"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6"> 
            Transform Your Business Operations with Strategic Digital Solutions 
          </h1> 
          <p className="text-xl text-blue-100 max-w-4xl mx-auto mb-10"> 
            We help established businesses streamline operations,reduce costs by 30-50%,and accelerate growth through proven digital transformation strategies. 
          </p> 
          <Link 
            to="/book-consultation" 
            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors" 
          > 
            Get Your Free Digital Assessment 
          </Link> 
        </div> 
      </section> 

      {/* Problem Section */} 
      <section className="py-16 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> 
              The Digital Transformation Challenge 
            </h2> 
            <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-8"> 
              Most businesses know they need to modernize,but struggle with: 
            </p> 
          </div> 
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12"> 
            {[ 
              {title: 'Disconnected Systems',description: 'Multiple tools that don\'t communicate,creating data silos and inefficiencies'},
              {title: 'Manual Processes',description: 'Time-consuming tasks that could be automated,costing thousands in productivity'},
              {title: 'Scaling Limitations',description: 'Current systems can\'t support growth without exponential cost increases'},
              {title: 'Competitive Disadvantage',description: 'Competitors using modern systems are moving faster and more efficiently'} 
            ].map((challenge,index)=> ( 
              <div key={index} className="bg-gray-50 p-6 rounded-xl"> 
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{challenge.title}</h3> 
                <p className="text-gray-600">{challenge.description}</p> 
              </div> 
            ))} 
          </div> 
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center"> 
            <p className="text-lg text-gray-700"> 
              <strong>According to McKinsey research,</strong> companies that successfully implement digital transformation see <span className="text-blue-600 font-semibold"> 23% higher revenue growth</span> and <span className="text-blue-600 font-semibold"> 12% higher market valuation</span>. 
            </p> 
          </div> 
        </div> 
      </section> 

      {/* Solution Section */} 
      <section className="py-16 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6"> 
              Strategic Digital Transformation That Delivers Results 
            </h2> 
            <p className="text-xl text-gray-600 max-w-4xl mx-auto"> 
              Our proven methodology transforms businesses systematically,ensuring maximum ROI and minimal disruption 
            </p> 
          </div> 
          <div className="grid lg:grid-cols-2 gap-12 items-center"> 
            <div className="space-y-8"> 
              <div className="flex items-start space-x-4"> 
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div> 
                <div> 
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Digital Readiness Assessment</h3> 
                  <p className="text-gray-600"> 
                    We analyze your current systems,processes,and goals to create a customized transformation roadmap. 
                  </p> 
                </div> 
              </div> 
              <div className="flex items-start space-x-4"> 
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div> 
                <div> 
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Strategic Implementation</h3> 
                  <p className="text-gray-600"> 
                    We implement integrated solutions that automate workflows,connect systems,and eliminate bottlenecks. 
                  </p> 
                </div> 
              </div> 
              <div className="flex items-start space-x-4"> 
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div> 
                <div> 
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Optimization & Scaling</h3> 
                  <p className="text-gray-600"> 
                    We continuously optimize performance and prepare your systems for sustainable growth. 
                  </p> 
                </div> 
              </div> 
            </div> 
            <div className="bg-white p-8 rounded-xl shadow-lg"> 
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What Makes Us Different</h3> 
              <ul className="space-y-4"> 
                {[ 
                  'Proven Methodology - Based on successful transformations across 50+ businesses',
                  'Integration Expertise - We connect your existing systems rather than replacing everything',
                  'ROI Focus - Every recommendation includes clear cost-benefit analysis',
                  'Ongoing Support - We ensure long-term success with continuous optimization' 
                ].map((item,index)=> ( 
                  <li key={index} className="flex items-start space-x-3"> 
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> 
                    <span className="text-gray-700">{item}</span> 
                  </li> 
                ))} 
              </ul> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* Services Section */} 
      <section className="py-16 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> 
              Our Digital Transformation Services 
            </h2> 
          </div> 
          <div className="space-y-12"> 
            {services.map((service,index)=> ( 
              <div key={index} className="bg-gray-50 rounded-2xl p-8 lg:p-12"> 
                <div className="grid lg:grid-cols-2 gap-12 items-start"> 
                  <div> 
                    <div className="flex items-center space-x-4 mb-6"> 
                      <div className="p-3 bg-blue-100 rounded-xl"> 
                        <SafeIcon icon={service.icon} className="h-8 w-8 text-blue-600" /> 
                      </div> 
                      <h3 className="text-2xl font-bold text-gray-900">{service.title}</h3> 
                    </div> 
                    <p className="text-lg text-gray-600 mb-8">{service.description}</p> 
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4"> 
                      <h4 className="font-semibold text-gray-900 mb-2">Ideal For:</h4> 
                      <p className="text-gray-700">{service.idealFor}</p> 
                    </div> 
                  </div> 
                  <div className="bg-white rounded-xl p-6 shadow-lg"> 
                    <h4 className="text-xl font-semibold text-gray-900 mb-4">What You Get:</h4> 
                    <ul className="space-y-3"> 
                      {service.benefits.map((benefit,idx)=> ( 
                        <li key={idx} className="flex items-start space-x-3"> 
                          <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> 
                          <span className="text-gray-700">{benefit}</span> 
                        </li> 
                      ))} 
                    </ul> 
                  </div> 
                </div> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Results Section */} 
      <section className="py-16 bg-gray-50"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> 
              Real Results from Digital Transformation 
            </h2> 
          </div> 
          <div className="grid md:grid-cols-3 gap-8"> 
            {results.map((result,index)=> ( 
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg"> 
                <h3 className="text-xl font-bold text-gray-900 mb-6">{result.company}</h3> 
                <ul className="space-y-3"> 
                  {result.improvements.map((improvement,idx)=> ( 
                    <li key={idx} className="flex items-start space-x-3"> 
                      <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" /> 
                      <span className="text-gray-700">{improvement}</span> 
                    </li> 
                  ))} 
                </ul> 
              </div> 
            ))} 
          </div> 
        </div> 
      </section> 

      {/* Investment Section */} 
      <section className="py-16 bg-white"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"> 
          <div className="text-center mb-12"> 
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"> 
              Investment & ROI 
            </h2> 
          </div> 
          <div className="grid md:grid-cols-3 gap-8"> 
            <div className="bg-gray-50 p-8 rounded-xl"> 
              <h3 className="text-xl font-bold text-gray-900 mb-4">Digital Transformation Assessment</h3> 
              <p className="text-gray-600 mb-6"> 
                Comprehensive analysis of your current systems and transformation opportunities. 
              </p> 
              <ul className="space-y-2 mb-6"> 
                {[ 
                  'Complete business process audit',
                  'Custom transformation roadmap',
                  'ROI projections and timeline',
                  'Implementation recommendations' 
                ].map((item,idx)=> ( 
                  <li key={idx} className="flex items-start space-x-2"> 
                    <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> 
                    <span className="text-sm text-gray-700">{item}</span> 
                  </li> 
                ))} 
              </ul> 
            </div> 
            <div className="bg-blue-50 p-8 rounded-xl border-2 border-blue-200"> 
              <h3 className="text-xl font-bold text-gray-900 mb-4">Implementation Services</h3> 
              <p className="text-gray-600 mb-6"> 
                Full-service digital transformation with ongoing support. 
              </p> 
              <ul className="space-y-2 mb-6"> 
                {[ 
                  'Custom solution design and development',
                  'System integration and data migration',
                  'Staff training and change management',
                  '90-day optimization and support period' 
                ].map((item,idx)=> ( 
                  <li key={idx} className="flex items-start space-x-2"> 
                    <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> 
                    <span className="text-sm text-gray-700">{item}</span> 
                  </li> 
                ))} 
              </ul> 
            </div> 
            <div className="bg-gray-50 p-8 rounded-xl"> 
              <h3 className="text-xl font-bold text-gray-900 mb-4">Ongoing Optimization</h3> 
              <p className="text-gray-600 mb-6"> 
                Continuous improvement and scaling support. 
              </p> 
              <ul className="space-y-2 mb-6"> 
                {[ 
                  'Monthly performance analysis',
                  'System updates and enhancements',
                  'New integration opportunities',
                  'Strategic technology planning' 
                ].map((item,idx)=> ( 
                  <li key={idx} className="flex items-start space-x-2"> 
                    <SafeIcon icon={FiCheck} className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" /> 
                    <span className="text-sm text-gray-700">{item}</span> 
                  </li> 
                ))} 
              </ul> 
            </div> 
          </div> 
          <div className="text-center mt-12"> 
            <div className="bg-green-50 border border-green-200 rounded-xl p-6 inline-block"> 
              <p className="text-lg text-gray-700"> 
                <strong className="text-green-700">ROI Guarantee:</strong> Most clients see 200%+ ROI within 12 months,or we'll work with you until you do. 
              </p> 
            </div> 
          </div> 
        </div> 
      </section> 

      {/* CTA Section */} 
      <section className="py-16 bg-blue-900"> 
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"> 
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6"> 
            Ready to Transform Your Business Operations? 
          </h2> 
          <p className="text-xl text-blue-100 mb-8 max-w-4xl mx-auto"> 
            Schedule your complimentary Digital Transformation Assessment and discover how much time and money you could save with the right systems in place. 
          </p> 
          <div className="bg-white/10 rounded-xl p-6 max-w-2xl mx-auto mb-10"> 
            <h3 className="text-xl font-semibold text-white mb-4">What You'll Get:</h3> 
            <ul className="text-left space-y-2"> 
              {[ 
                '60-minute analysis of your current operations',
                'Custom transformation roadmap',
                'ROI projections for recommended improvements',
                'No-obligation consultation with our transformation experts' 
              ].map((item,idx)=> ( 
                <li key={idx} className="flex items-start space-x-3"> 
                  <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-300 mt-0.5 flex-shrink-0" /> 
                  <span className="text-blue-100">{item}</span> 
                </li> 
              ))} 
            </ul> 
          </div> 
          <Link 
            to="/book-consultation" 
            className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-50 transition-colors" 
          > 
            Schedule Your Free Assessment 
          </Link> 
        </div> 
      </section> 
    </div> 
  );
}

export default DigitalTransformation;